import { useState } from 'react';
import { Outlet, NavLink, useParams, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  MessageSquare,
  Code,
  FolderTree,
  Terminal,
  Globe,
  Settings,
  Play,
  Square,
  RotateCw,
  Hammer,
  ChevronLeft,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';
import { useProject, useProjectActions } from '@/hooks/useProject';
import { useProjectStatus } from '@/hooks/useProjectStatus';

interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
}

export function ProjectLayout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(slug!);
  const { start, stop, restart, rebuild } = useProjectActions(slug!);
  const { projectStatus: realTimeStatus } = useProjectStatus(slug, { pollInterval: 10000 });
  const [activePanel, setActivePanel] = useState<'chat' | 'files' | 'terminal'>('chat');

  const handleRebuild = () => {
    // Navigate to chat page first to show the terminal
    navigate(`/projects/${slug}/chat`);
    // Then trigger the rebuild
    rebuild.mutate();
  };

  if (isLoading || !project) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tabs: TabItem[] = [
    { id: 'overview', label: 'Overview', icon: <Code className="w-4 h-4" />, href: `/projects/${slug}` },
    { id: 'chat', label: 'AI Chat', icon: <MessageSquare className="w-4 h-4" />, href: `/projects/${slug}/chat` },
    { id: 'files', label: 'Files', icon: <FolderTree className="w-4 h-4" />, href: `/projects/${slug}/files` },
    { id: 'terminal', label: 'Terminal', icon: <Terminal className="w-4 h-4" />, href: `/projects/${slug}/terminal` },
    { id: 'domains', label: 'Domains', icon: <Globe className="w-4 h-4" />, href: `/projects/${slug}/domains` },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" />, href: `/projects/${slug}/settings` },
  ];

  // Use real-time status if available, otherwise fall back to database status
  const displayStatus = realTimeStatus || project.status;
  const isRunning = displayStatus === 'Running';
  const isStopped = displayStatus === 'Stopped';

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-dark-900">
      {/* Project Header */}
      <header className="h-14 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 px-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <NavLink
            to="/dashboard"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-dark-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </NavLink>

          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {project.name}
            </h1>
            <StatusBadge status={displayStatus} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Project Actions */}
          {isStopped ? (
            <Tooltip content="Start Project">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Play className="w-4 h-4" />}
                onClick={() => start.mutate()}
                isLoading={start.isPending}
              >
                Start
              </Button>
            </Tooltip>
          ) : isRunning ? (
            <>
              <Tooltip content="Stop Project">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Square className="w-4 h-4" />}
                  onClick={() => stop.mutate()}
                  isLoading={stop.isPending}
                >
                  Stop
                </Button>
              </Tooltip>
              <Tooltip content="Restart Project">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCw className="w-4 h-4" />}
                  onClick={() => restart.mutate()}
                  isLoading={restart.isPending}
                >
                  Restart
                </Button>
              </Tooltip>
            </>
          ) : null}

          <Tooltip content="Rebuild Project">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Hammer className="w-4 h-4" />}
              onClick={handleRebuild}
              isLoading={rebuild.isPending}
            >
              Rebuild
            </Button>
          </Tooltip>

          {project.previewUrl && (
            <Button
              variant="primary"
              size="sm"
              rightIcon={<ExternalLink className="w-4 h-4" />}
              onClick={() => window.open(project.previewUrl!, '_blank')}
            >
              Open Preview
            </Button>
          )}
        </div>
      </header>

      {/* Tabs Navigation */}
      <nav className="h-12 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 px-4 flex items-center gap-1 flex-shrink-0">
        {tabs.map((tab) => (
          <NavLink
            key={tab.id}
            to={tab.href}
            end={tab.id === 'overview'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
              )
            }
          >
            {tab.icon}
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Content */}
      <main className="flex-1 overflow-hidden">
        <Outlet context={{ project }} />
      </main>
    </div>
  );
}
